import type { Key, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Selection } from 'react-aria-components';
import { enableSemverOperatorsForMetricFilters } from '@gonfalon/dogfood-flags/dogfood-flags';
import { operators } from '@gonfalon/metrics/operators';
import type { ContextAttributeFilter, EventPropertyFilter, MetricFilter } from '@gonfalon/metrics/types';
import {
  Button,
  ComboBox,
  DialogTrigger,
  FieldError,
  FieldGroup,
  Group,
  IconButton,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';
import { debounce } from 'lodash';

import { FilterSelectionPopover } from '../FilterSelectionPopover';
import { FilterValuesAutocomplete } from '../FilterValuesAutocomplete';
import { defaultContextAttributeFilter, defaultEventPropertyFilter } from '../formUtils';
import { TypeAwareTag } from '../TypeAwareTag/TypeAwareTag';

import styles from './FilterBase.module.css';

interface FilterProps {
  filter: ContextAttributeFilter | EventPropertyFilter;
  onUpdate: (filter: MetricFilter) => void;
  onRemove: () => void;
  showSubFilter?: boolean;
  onAddSubFilter?: (chosenFilter: MetricFilter) => void;
  children?: ReactNode;
  isOperatorDisabled?: boolean;
  attributePlaceholder: string;
  isAttributeDisabled?: boolean;
}

export const FilterBase: React.FC<FilterProps> = ({
  filter,
  onUpdate,
  onRemove,
  showSubFilter,
  onAddSubFilter,
  children,
  isOperatorDisabled,
  attributePlaceholder,
  isAttributeDisabled,
}) => {
  const handleRemoveTag = useCallback(
    (keys: Set<Key>) => {
      onUpdate({ ...filter, values: filter.values.filter((value) => !keys.has(String(value))) });
    },
    [filter, onUpdate],
  );

  const handleChangeTagType = useCallback(
    (index: number, castedValue: string | number | boolean) => {
      onUpdate({
        ...filter,
        values: filter.values.map((v, i) => {
          if (i === index) {
            return castedValue;
          }

          return v;
        }),
      });
    },
    [onUpdate, filter],
  );

  const handleChangeOperator = useCallback(
    (key: Key | null) => {
      if (key) {
        const operator = operators.find((op) => op.textValue === key.toString());
        if (operator) {
          onUpdate({ ...filter, op: operator.op, negate: operator.negate });
        }
      }
    },
    [filter, onUpdate],
  );

  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys !== 'all') {
        const values = Array.from(keys);
        // The incoming keys are string types, although the filter values can
        // be of type string number or boolean.
        if (values.length < filter.values.length) {
          // a value was removed
          const removedIndex = filter.values.findIndex((v, i) => String(v) !== values[i]);

          onUpdate({
            ...filter,
            values: filter.values.filter((_, i) => i !== removedIndex),
          });
        } else {
          // a value was added
          const newItem = values.at(-1);

          if (typeof newItem === 'string') {
            const newItemCasted = (function (s) {
              if (typeof Number(s) === 'number' && !isNaN(Number(s))) {
                return Number(s);
              } else if (typeof s === 'string' && (s === 'true' || s === 'false')) {
                return s === 'true' ? true : false;
              } else {
                return s;
              }
            })(newItem);

            onUpdate({
              ...filter,
              values: filter.values.concat(newItemCasted),
            });
          }
        }
      }
    },
    [filter, onUpdate],
  );

  const debouncedAttributeChange = useMemo(
    () => debounce((value: string) => onUpdate({ ...filter, attribute: value }), 300),
    [filter, onUpdate],
  );

  const [showErrors, setShowErrors] = useState(false);

  const errorMessage =
    showErrors && (!filter.attribute || !filter.values || filter.values.length === 0)
      ? 'Please fill all required fields'
      : undefined;

  return (
    <div className={styles.contextAttributeFilter}>
      <FieldGroup className={styles.contextAttributeFilterFieldGroup} errorMessage={errorMessage}>
        <div className={styles.contextKindsRow}>
          {children}
          <ComboBox
            allowsCustomValue
            defaultInputValue={filter.attribute}
            onInputChange={debouncedAttributeChange}
            aria-label={attributePlaceholder}
            isDisabled={isAttributeDisabled}
            isInvalid={showErrors && !filter.attribute}
            className={styles.selectContextAttribute}
          >
            <Group>
              <Input placeholder={attributePlaceholder} onBlur={() => setShowErrors(true)} />
              <FieldError />
            </Group>
            <Popover>
              <ListBox />
            </Popover>
          </ComboBox>
          <ComboBox
            name="Operator"
            selectedKey={operators.find((op) => op.op === filter.op && op.negate === filter.negate)?.textValue}
            onSelectionChange={handleChangeOperator}
            aria-label="Select an operator"
            isDisabled={isOperatorDisabled}
            className={styles.selectOperator}
            isInvalid={!filter.op}
          >
            <Group>
              <Input placeholder="Select an operator" />
              <Icon name="chevron-down" size="small" aria-hidden="true" />
            </Group>
            <Popover>
              <ListBox className={styles.selectOperatorListBox}>
                {operators
                  .filter((op) => !op.op.startsWith('semVer') || enableSemverOperatorsForMetricFilters())
                  .map((op) => (
                    <ListBoxItem id={op.textValue} key={op.textValue}>
                      {op.textValue}
                    </ListBoxItem>
                  ))}
              </ListBox>
            </Popover>
          </ComboBox>
        </div>

        <div className={styles.addValueButtonContainer}>
          <DialogTrigger onOpenChange={(isOpen) => setShowErrors(!isOpen)}>
            <Button
              size="small"
              aria-label="Add value"
              variant="minimal"
              className={clsx(styles.addValueButton, {
                [styles.addValueButtonInvalid]: showErrors && (!filter.values || filter.values.length === 0),
              })}
            >
              <Icon name="add-circle-outline" size="small" aria-hidden="true" />
              Choose value(s)
            </Button>
            <Popover placement="bottom left" className={styles.filterValuesPopover}>
              <FilterValuesAutocomplete
                onSelectionChange={handleSelectionChange}
                selectedKeys={filter.values.map(String)}
              />
            </Popover>
          </DialogTrigger>
          {filter.values.length > 0 &&
            filter.values.map((value, i) => (
              <TypeAwareTag
                key={String(value)}
                value={value}
                onChange={(castedValue) => {
                  handleChangeTagType(i, castedValue);
                }}
                onRemove={() => handleRemoveTag(new Set([String(value)]))}
              />
            ))}
        </div>
        {showSubFilter && onAddSubFilter && (
          <FilterSelectionPopover
            onSelectContextKind={() => onAddSubFilter(defaultContextAttributeFilter)}
            onSelectEventProperty={() => onAddSubFilter(defaultEventPropertyFilter)}
          >
            <Button variant="minimal" className={styles.iconSpan}>
              <Icon name="filter-tune" size="tiny" aria-hidden="true" /> Add another filter
            </Button>
          </FilterSelectionPopover>
        )}
      </FieldGroup>
      <div className={styles.actionContainer}>
        <IconButton icon="cancel" size="small" onPress={onRemove} aria-label="Remove filter" variant="minimal" />
      </div>
    </div>
  );
};
